import { pendingCommunityTemplateIdAtom } from '@/atoms/templateCommunityAtoms';
import { useMutateImportTemplateFromGallery } from '@/hooks/mutation/useMutateImportTemplateFromGallery';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useTutorial } from '@/hooks/useTutorial';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { cn } from '@/lib/cn';
import { SupportedLocale } from '@/utils/i18n/supportedLocales';
import { Button } from '@heidi/design-system/button';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { TemplatePages } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { Loader2, Plus as PlusIcon } from 'lucide-react';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

interface AddToLibraryButtonProps {
  templateId?: string;
  templateAlreadyExists: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'touch';
  className?: string;
}

export const AddToLibraryButton = ({
  templateId,
  templateAlreadyExists,
  size = 'md',
  className,
}: AddToLibraryButtonProps) => {
  const { locale: intlLocale } = useIntl();
  const locale = intlLocale as SupportedLocale;
  const { data: userData } = useQueryUser();
  const { tutorialStepCurrentPage, panelOpen } = useTutorial();
  const router = useRouter();
  const { isAuthenticated: isTokenAuthenticated } = useIsAuthenticated();
  const setPendingCommunityTemplateId = useSetAtom(pendingCommunityTemplateIdAtom);

  const isAuthenticated = isTokenAuthenticated && !!userData;

  const shouldHighlightAddToLibrary = useMemo(
    () =>
      tutorialStepCurrentPage?.enum === TemplatePages.ADD_TO_YOUR_LIBRARY && panelOpen,
    [tutorialStepCurrentPage?.enum, panelOpen]
  );

  const { mutate: importTemplateFromGallery, isPending } =
    useMutateImportTemplateFromGallery();

  const handleClick = () => {
    if (!templateId) {
      return;
    }
    trackButtonClicked({
      screenName: ScreenName.TemplateCommunityDetails,
      action: ButtonAction.CommunityTemplateImport,
      label: isAuthenticated ? 'Add to my library' : 'Use Template',
    });
    if (isAuthenticated) {
      void importTemplateFromGallery({
        input: { templateId },
        saveAnyway: false,
      });
      return;
    }
    setPendingCommunityTemplateId(templateId);
    void router.push(
      `/onboarding?post_login_redirect_url=${encodeURIComponent('/scribe/my-templates/community-import')}`
    );
  };

  return (
    <Button
      data-testid="community-add-to-library-button"
      size={size}
      className={cn(
        locale === SupportedLocale.DeDe && 'text-xs',
        shouldHighlightAddToLibrary && 'animation-ping',
        className
      )}
      onClick={handleClick}
      disabled={templateAlreadyExists || (isAuthenticated && isPending)}
    >
      {isAuthenticated && isPending ? <Loader2 className="animate-spin" /> : <PlusIcon />}
      {templateAlreadyExists ? (
        <FormattedMessage
          defaultMessage="Already in your library"
          id="+geA9TIYUn"
          description="Button label when a community template is already in the user’s library"
        />
      ) : userData ? (
        <FormattedMessage
          defaultMessage="Add to my library"
          id="4e6MHpbUaF"
          description="Button label for adding a community template to the user’s library"
        />
      ) : (
        <FormattedMessage
          defaultMessage="Use template"
          id="VM1qiCYtr3"
          description="Button label for signing in to use a community template"
        />
      )}
    </Button>
  );
};
